import DynamicModal from 'components/DynamicModal';
import { useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useState } from 'react';
import { Button, UncontrolledTooltip } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import RepositoryDetailModalContent from './RepositoryDetailModalContent';
import { forceReloadRepoDataAtom } from '../state';

interface Props {
  repoId: number;
  isOpen: boolean;
  isActive: boolean;
  isAccountActive: boolean;
  toggle: () => void;
  handleActivateDeactivateRepository: (isActive: boolean) => void;
}

const RepositoryDetailModal: React.FC<Props> = ({
  repoId,
  isActive,
  isAccountActive,
  isOpen,
  handleActivateDeactivateRepository,
  toggle,
}) => {
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteConfirmModalOpen, setdeleteConfirmModalOpen] = useState(false);

  const companyId = useAtomValue(companyIdAtom);
  // const setForceRerenderUI = useSetAtom(forceRerenderUIAtom);
  const setForceReloadRepoData = useSetAtom(forceReloadRepoDataAtom);

  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDeleteRepository = async () => {
    const data = {
      company_id: companyId,
    };
    setDeleteLoading(true);
    callApi(`sast/repositories/delete/${repoId}/`, 'DELETE', {}, data)
      .then((res) => {
        if (res.status === 204) {
          toggle();
          setForceReloadRepoData(Math.random().toString());
          showSuccessToast(`Repository deleted successfully.`);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(`Failed to delete repository:`, err);
        showFailureToast(`Failed to delete repository. ${err?.detail}`);
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  const confirmModal = () => {
    return (
      <DynamicModal
        isOpen={confirmModalOpen}
        toggle={() => setConfirmModalOpen(!confirmModalOpen)}
        title={`Confirm ${isActive ? 'Deactivation' : 'Activation'}`}
        content={
          <div>
            <p>
              Are you sure you want to {isActive ? 'deactivate' : 'activate'}{' '}
              this repository?
            </p>
          </div>
        }
        size="sm"
        footerActions={
          <>
            <Button
              color="secondary"
              onClick={() => setConfirmModalOpen(!confirmModalOpen)}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={() => {
                handleActivateDeactivateRepository(!isActive);
                setConfirmModalOpen(!confirmModalOpen);
              }}
            >
              Confirm
            </Button>
          </>
        }
      />
    );
  };

  const deleteConfirmModal = () => {
    return (
      <DynamicModal
        isOpen={deleteConfirmModalOpen}
        toggle={() => setdeleteConfirmModalOpen(!deleteConfirmModalOpen)}
        title={`Confirm delete`}
        content={
          <div>
            <p>Are you sure you want to delete this repository?</p>
          </div>
        }
        size="sm"
        footerActions={
          <>
            <Button
              color="secondary"
              onClick={() => setdeleteConfirmModalOpen(!deleteConfirmModalOpen)}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              disabled={deleteLoading}
              onClick={() => {
                handleDeleteRepository();
                setdeleteConfirmModalOpen(!deleteConfirmModalOpen);
              }}
            >
              Confirm
            </Button>
          </>
        }
      />
    );
  };

  return (
    <>
      <DynamicModal
        isOpen={isOpen}
        toggle={toggle}
        title="Repository Details"
        content={<RepositoryDetailModalContent id={repoId} />}
        footerActions={
          <>
            <Button color="secondary" onClick={toggle}>
              Cancel
            </Button>
            {isActive ? (
              <>
                <span id="deactivationButton">
                  <Button
                    disabled={!isAccountActive}
                    color="danger"
                    onClick={() => setConfirmModalOpen(!confirmModalOpen)}
                  >
                    Deactivate
                  </Button>
                </span>
                {!isAccountActive && (
                  <UncontrolledTooltip
                    placement="top"
                    target="deactivationButton"
                  >
                    The repository account is inactive.
                  </UncontrolledTooltip>
                )}
              </>
            ) : (
              <div className="d-flex">
                <span id="activationButton" className="mr-3">
                  <Button
                    disabled={!isAccountActive}
                    color="primary"
                    onClick={() => setConfirmModalOpen(!confirmModalOpen)}
                  >
                    Activate
                  </Button>
                </span>
                {!isAccountActive && (
                  <UncontrolledTooltip
                    placement="top"
                    target="activationButton"
                  >
                    The repository account is inactive.
                  </UncontrolledTooltip>
                )}
                <span id="deleteButton">
                  <Button
                    disabled={!isAccountActive || deleteLoading}
                    color="danger"
                    onClick={() =>
                      setdeleteConfirmModalOpen(!deleteConfirmModalOpen)
                    }
                  >
                    Delete
                  </Button>
                </span>
                {!isAccountActive && (
                  <UncontrolledTooltip placement="top" target="deleteButton">
                    Delete this repository.
                  </UncontrolledTooltip>
                )}
              </div>
            )}
          </>
        }
      />
      {confirmModal()}
      {deleteConfirmModal()}
    </>
  );
};

export default RepositoryDetailModal;
